import {getButtonsForModule, getUsersWithAuthorization, prepareDefaultMail} from "./utils"
import _ from "lodash";
import moment from "moment/moment";
import {basicContext} from "../../../utils/contextUtils";
import async from "async";
import Errors from "../../../utils/Errors";
import {decrypt} from "../../../utils/crypto";
import {getCNDANotificationData} from "../utils/notifications";

export const entities = [
    {
        name: 'Payment',
        facets: ['comments'],
        fields: [
            {type: 'Need'},
            {
                path: 'natureOfHelp',
                fieldPath: ['need.natureOfHelp.name'],
                $f: function(payment, context, callback) {
                    return callback(null, _.get(payment, 'need.natureOfHelp.name'))
                }
            },
            {
                path: 'natureOfHelpWithUnit',
                fieldPath: ['need.natureOfHelp.name', 'need.natureOfHelp.unit.name'],
                $f: function(payment, context, callback) {
                    return callback(null, _.get(payment, 'need.natureOfHelp.name') + ' (' + _.get(payment, 'need.natureOfHelp.unit.name') + ')')
                }
            },
            {
                path: 'reliedFundApplicationNumber',
                fieldPath: ['need.reliefFund.applicationNumber'],
                f: function() {
                    return this.need?.reliefFund.applicationNumber
                }
            },
            {
                path: 'awardedAmountInEuros',
                fieldPath: ['need.awardedAmountInEuros'],
                f: function() {
                    return this.need?.awardedAmountInEuros
                }
            },
            {
                path: 'awardedAmountInKilograms',
                fieldPath: ['need.awardedAmountInKilograms'],
                f: function() {
                    return this.need?.awardedAmountInKilograms
                }
            },
            {
                path: 'organizationName',
                fieldPath: ['need.reliefFund.organizationName'],
                f: function() {
                    return this.need?.reliefFund?.organizationName
                }
            },
            {
                path: 'organizationTradingName',
                fieldPath: ['need.reliefFund.organizationTradingName'],
                f: function() {
                    return this.need?.reliefFund?.organizationTradingName
                }
            },
            {
                path: 'proposedAmountInEuros',
                type: 'integer',

            },
            {path: 'proposedAmountInKilograms', type: 'integer'},
            {path: 'account', type: 'Bank', nullable: true},
            {path: 'thirdPartyRecipient', type: 'Organization', nullable: true},
            {path: 'status', type: 'ReliefFundPaymentStatus', nullable: true},
            {path: 'paymentDate', type: 'date', nullable: true},
            {
                path: 'buttons',
                fieldPath: ['status.id'],
                $f: async function (payment, context, callback) {
                    const buttons = await getButtonsForModule(payment, context);
                    callback(null, buttons);
                }
            },
            {
                path: 'object',
                fieldPath: ['need.reliefFund.object'],
                f: function() {
                    return this.need?.reliefFund?.object
                }
            },
            {
                path: 'balanceInEuros',
                fieldPath: [
                    'need.awardedAmountInEuros',
                    'need.reliefFund.payments.need.id',
                    'need.reliefFund.payments.status',
                    'need.reliefFund.payments.proposedAmountInEuros'
                ],
                f: function() {
                    const needPayments = (this.need.reliefFund.payments || [])
                        .filter(payment => !['refused', 'draft', 'incomplete'].includes(_.get(payment, 'status.id'))  && payment.need?.id === this.need.id)
                        .reduce((acc, payment) => {
                            if(!payment.proposedAmountInEuros) return acc
                            return acc + Number.parseInt(payment.proposedAmountInEuros)
                        }, 0)
                    return this.need?.awardedAmountInEuros - needPayments
                }
            },
            {
                path: 'balanceInKilograms',
                fieldPath: [
                    'need.awardedAmountInKilograms',
                    'need.reliefFund.payments.need.id',
                    'need.reliefFund.payments.status',
                    'need.reliefFund.payments.proposedAmountInKilograms'
                ],
                f: function() {
                    const needPayments = (this.need.reliefFund.payments || [])
                        .filter(payment => !['refused', 'draft', 'incomplete'].includes(_.get(payment, 'status.id'))  && payment.need?.id === this.need.id)
                        .reduce((acc, payment) => {
                            if(!payment.proposedAmountInKilograms) return acc
                            return acc + Number.parseInt(payment.proposedAmountInKilograms)
                        }, 0)
                    return this.need?.awardedAmountInKilograms - needPayments
                }
            },
            {
                path: "noDeleteButtonAccess",
                fieldPath: ['status.id'],
                $f: function (payment, context, callback) {
                    const noDeleteAccess = !['draft', 'incomplete'].includes(_.get(payment, 'status.id'))

                    // Call the callback function with the result
                    // true means no delete access, false means delete access is allowed
                    callback(null, noDeleteAccess)
                }
            }
        ],
        filters: [
            {
                name: 'byStatus',
                path: 'statuses',
                object: 'ReliefFundPaymentStatus',
                display: 'name',
                type: 'tags',
                default: [{id: 'ongoing'}],
                filters: [
                    {
                        path: 'notDraft',
                        match: object => object.id !== 'draft'
                    }
                ],
                client: true,
                width: 5,
                clearable: true,
                query: (context) => {
                    const statuses = _.get(context, 'data.statuses', []) || []
                    return statuses.length ? { status: {$in: statuses.map(status => status.id)} } : {}
                }
            },
            {
                name: 'byAcceptedFiles',
                path: 'reliefFund',
                tKey: 'reliefFundFile',
                object: 'ReliefFund',
                filters: [
                    'isAcceptedOrClosed', 'hasNonDraftPayment',
                    //'isAccepted', 'hasOngoingPayment'
                ],
                display: 'fullName',
                client: true,
                width: 7,
                clearable: true,
                default: {id: null},
                query: (context) => {
                    const reliefFundId = _.get(context, 'data.reliefFund.id')
                    return reliefFundId
                        ? { reliefFund: new global.ObjectID(reliefFundId) }
                        : {}
                }
            },
            {
                name: 'userHasLiberationAuthorization',
                async: true,
                isDefault: false,
                query: async (context, callback) => {
                    const userId = _.get(context, 'user.id')

                    // Determine the authorization property based on the current module ID
                    try {
                        const cUser = await global.app.C.CUser.get({kpUser: new global.ObjectID(userId)}, {
                            ...basicContext(context),
                            fieldPath: ['id', 'functions.liberationAuthorization']
                        })
                        const userHasAuthorization = cUser.functions.some(userFunction => !!userFunction['liberationAuthorization'])

                        return callback( null, userHasAuthorization ? {} : {_id: null} )
                    } catch (e) {
                        callback(e)
                    }

                }
            }
        ],
        authorizationsValidation: function (cUser, context, callback) {
            const moduleId = context.clientContext.moduleId
            const authorizationProperty = 'liberationAuthorization'

            const hasAuthorization = cUser.functions.some(userFunction => !!userFunction[authorizationProperty])

            if (moduleId === 'm-C-reliefFundPaymentValidation' && !hasAuthorization) {
                return callback(new Errors.ValidationError(`${authorizationProperty}Denied`))
            }

            return callback()
        },
        validateSave: async function (newObject, oldObject, context, callback) {
            const cUser = await global.app.C.CUser.get({kpUser: new global.ObjectID(context.user.id)}, {
                ...basicContext(context),
                fieldPath: [
                    'id',
                    'functions.id',
                    'functions.submissionAuthorization',
                    'functions.studyAuthorization',
                    'functions.decisionAuthorization',
                    'functions.solicitationsManagementAuthorization',
                    'functions.liberationAuthorization',
                    'organization.id',
                    'shelters.id'
                ]
            })
            async.series([
                callback => this.authorizationsValidation(cUser, context, callback)
            ], callback)
        },
        beforeSave: function(newObject, oldObject, context, callback) {
            const buttonAction = context.action
            let action = ''
            switch (buttonAction) {
                case 'validate':
                    newObject.status.id = 'validated'
                    action = 'Libération validée'
                    break
                case 'refuse':
                    newObject.status.id = 'refused'
                    action = 'Libération refusée'
                    break
                case 'justify':
                    newObject.status.id = 'incomplete'
                    action = 'Libération à justifier'
                    break
            }

            const unit = newObject.need.natureOfHelp.unit?.id ? newObject.need.natureOfHelp.unit.id : newObject.need.natureOfHelp.unit

            const commentText = unit === 'kilogram' ?
                `${action} : ${newObject.proposedAmountInKilograms} kg` :
                `${action} : ${newObject.proposedAmountInEuros} € - ${newObject.account.name}`

            newObject.comments.push({
                user: _.pick(context.user, ['id', 'name']),
                text: commentText,
                date: moment().format("YYYY-MM-DD HH:mm:ss")
            })

            return callback(null, newObject, oldObject)
        },
        afterSave: function(newObject, oldObject, context, callback) {
            getCNDANotificationData(context.group.id)
                .then(data => global.ioSocket.emit('afterSave', data))

            callback()
        }
        /*
        afterSave: async function(newObject, oldObject, context, callback) {
            const recipients = await getUsersWithAuthorization('paymentAuthorization')
            const defaultMail = prepareDefaultMail(context)

            const statusId = _.get(newObject, 'status.id')
            const content = {}

            switch (statusId) {
                case 'validated':
                    content.template = 'payer_validation_notification.html'
                    content.subject = `Paiement validé`
                    break
                case 'refused':
                    content.template = 'payer_rejection_notification.html'
                    content.subject = `Paiement refusé`
                    break
                case 'incomplete':
                    content.template = 'payer_justification_notification.html'
                    content.subject = `Demande de justification`
            }

            const mails = recipients.map(recipient => {
                return _.defaults(
                    {
                        to: recipient.mail && decrypt(recipient.mail),
                        content: content.template,
                        subject: { template: content.subject },
                        context: {
                            firstname: recipient.firstname,
                            applicationNumber: newObject.reliedFundApplicationNumber
                        }
                    },
                    defaultMail
                )
            })

            return global.mailer.sendMail(mails, (error) => {
                console.log(error)
                return callback()
            })
        }
         */
    }
]
